/**
 * Theme Store
 *
 * Centralized theme management using Zustand for cross-context reactivity
 */

import { create } from 'zustand';

type Theme = 'light' | 'dark';

interface ThemeStore {
  theme: Theme;
  toggleTheme: () => void;
  setTheme: (theme: Theme) => void;
}

export const useThemeStore = create<ThemeStore>((set) => {
  // Initialize from localStorage or default to dark
  const stored = localStorage.getItem('luma-theme');
  const initialTheme: Theme = (stored === 'light' || stored === 'dark') ? stored : 'dark';

  // Apply initial theme to DOM
  const applyTheme = (theme: Theme) => {
    const root = document.documentElement;
    const body = document.body;

    // Remove both classes first
    root.classList.remove('light', 'dark');
    body.classList.remove('light', 'dark');

    // Add the current theme class
    root.classList.add(theme);
    body.classList.add(theme);

    // Persist to localStorage
    localStorage.setItem('luma-theme', theme);
  };

  // Apply initial theme
  applyTheme(initialTheme);

  return {
    theme: initialTheme,
    toggleTheme: () =>
      set((state) => {
        const newTheme = state.theme === 'dark' ? 'light' : 'dark';
        applyTheme(newTheme);
        return { theme: newTheme };
      }),
    setTheme: (theme) =>
      set(() => {
        applyTheme(theme);
        return { theme };
      })
  };
});
